import isEmail from "validator/lib/isEmail";
import { CargoDimensionMapper } from "./cargoDimensions";
import { QuoteCopy } from "./i18n/QuoteCopy";
import type { CopyKey } from "./i18n/copyTypes";
import { LIMITS, QUICK_FILL_LIMITS, QUOTE_ATTACHMENT_LIMITS, QUOTE_TEXT_LIMITS } from "./constants";
import type { UnsupportedRouteWarning } from "./applyExtracted";
import { DepartmentFieldAccess } from "./departmentFields";
import { PersonalEmailDomain } from "./personalEmail";
import { BoundedDate, EnteredDateValidator } from "./boundedDate";
import { todayISODate } from "./formModel";
import { buildSubject, generateEmailText } from "./summary";
import { TimeFieldValidator } from "./clockTime";
import { RouteLocationFormatValidator } from "./locationFormat";
import type { DepartmentConfig, FormState, ManualPage, TimeMode } from "./types";

export { RouteCityFormat, RouteLocationFormatValidator, RoutePostalFormat } from "./locationFormat";

// Map of field id -> human-readable error message.
export type Errors = Record<string, string>;

interface Range {
  min: number;
  max: number;
}

function inRange(value: string, { min, max }: Range): boolean {
  const n = Number(value);
  return Number.isFinite(n) && n >= min && n <= max;
}

const REQUIRED_KEYS = {
  loadCountry: "error.loadCountry",
  loadPostal: "error.loadPostal",
  loadCity: "error.loadCity",
  loadStreet: "error.loadStreet",
  loadHouseNumber: "error.loadHouseNumber",
  unloadCountry: "error.unloadCountry",
  unloadPostal: "error.unloadPostal",
  unloadCity: "error.unloadCity",
  unloadStreet: "error.unloadStreet",
  unloadHouseNumber: "error.unloadHouseNumber",
  pickupDate: "error.pickupDate",
  unloadDate: "error.unloadDate",
  totalWeight: "error.totalWeight",
  goods: "error.goods",
} as const satisfies Record<string, CopyKey>;

type RequiredStringField = keyof typeof REQUIRED_KEYS;

class RequiredStringFieldReader {
  value(form: FormState, id: RequiredStringField): string {
    switch (id) {
      case "loadCountry":
        return form.loadCountry;
      case "loadPostal":
        return form.loadPostal;
      case "loadCity":
        return form.loadCity;
      case "loadStreet":
        return form.loadStreet;
      case "loadHouseNumber":
        return form.loadHouseNumber;
      case "unloadCountry":
        return form.unloadCountry;
      case "unloadPostal":
        return form.unloadPostal;
      case "unloadCity":
        return form.unloadCity;
      case "unloadStreet":
        return form.unloadStreet;
      case "unloadHouseNumber":
        return form.unloadHouseNumber;
      case "pickupDate":
        return form.pickupDate;
      case "unloadDate":
        return form.unloadDate;
      case "totalWeight":
        return form.totalWeight;
      case "goods":
        return form.goods;
    }
  }
}

const requiredStringFields = Object.keys(REQUIRED_KEYS) as RequiredStringField[];
const requiredStringReader = new RequiredStringFieldReader();

interface RouteSideLocation {
  postal: string;
  city: string;
  postalId: "loadPostal" | "unloadPostal";
  cityId: "loadCity" | "unloadCity";
  message: CopyKey;
}

export class RouteLocationValidator {
  validate(form: FormState, config?: DepartmentConfig, copy: QuoteCopy = QuoteCopy.english()): Errors {
    const errors: Errors = {};
    if (this.usesCityOrPostal(config, "load")) {
      this.requireCityOrPostal(
        {
          postal: form.loadPostal,
          city: form.loadCity,
          postalId: "loadPostal",
          cityId: "loadCity",
          message: "error.loadCityOrPostal",
        },
        errors,
        copy,
      );
    }
    if (this.usesCityOrPostal(config, "unload")) {
      this.requireCityOrPostal(
        {
          postal: form.unloadPostal,
          city: form.unloadCity,
          postalId: "unloadPostal",
          cityId: "unloadCity",
          message: "error.unloadCityOrPostal",
        },
        errors,
        copy,
      );
    }
    return errors;
  }

  private usesCityOrPostal(config: DepartmentConfig | undefined, side: "load" | "unload"): boolean {
    if (!config) return true;
    const fields = new DepartmentFieldAccess(config);
    if (side === "load") {
      return !(fields.isRequired("loadPostal") && fields.isRequired("loadCity"));
    }
    return !(fields.isRequired("unloadPostal") && fields.isRequired("unloadCity"));
  }

  private requireCityOrPostal(side: RouteSideLocation, errors: Errors, copy: QuoteCopy): void {
    if (side.postal.trim() || side.city.trim()) return;
    const message = copy.t(side.message);
    errors[side.postalId] = message;
    errors[side.cityId] = message;
  }
}

class HomeCountryRouteValidator {
  validate(
    form: FormState,
    config: DepartmentConfig,
    copy: QuoteCopy,
    warning: UnsupportedRouteWarning | null,
  ): Errors {
    const home = new DepartmentFieldAccess(config).homeCountry();
    if (!home) return {};
    const load = form.loadCountry.trim().toUpperCase();
    const unload = form.unloadCountry.trim().toUpperCase();
    if (load === home || unload === home) return {};
    const pastedLoad = warning?.loadCountry.trim() ?? "";
    const pastedUnload = warning?.unloadCountry.trim() ?? "";
    if ((pastedLoad && pastedUnload) || (load && unload)) {
      const message = copy.t("route.unsupported", {
        loadCountry: pastedLoad || copy.countryName(load),
        unloadCountry: pastedUnload || copy.countryName(unload),
      });
      return { loadCountry: message, unloadCountry: message };
    }
    const errors: Errors = {};
    if (!load) errors.loadCountry = copy.t("error.loadCountry");
    if (!unload) errors.unloadCountry = copy.t("error.unloadCountry");
    return errors;
  }
}

export function validateForm(
  form: FormState,
  config: DepartmentConfig,
  copy: QuoteCopy = QuoteCopy.english(),
  unsupportedRoute: UnsupportedRouteWarning | null = null,
): Errors {
  const errors: Errors = {};
  const fields = new DepartmentFieldAccess(config);

  for (const id of requiredStringFields) {
    if (!fields.isPresent(id) || !fields.isRequired(id)) continue;
    if (!requiredStringReader.value(form, id).trim()) {
      errors[id] = copy.t(REQUIRED_KEYS[id]);
    }
  }

  const catalog = fields.countryCatalog();
  for (const country of [
    { id: "loadCountry" as const, value: form.loadCountry },
    { id: "unloadCountry" as const, value: form.unloadCountry },
  ]) {
    const value = country.value.trim();
    if (value && !catalog.toCode(value)) errors[country.id] = copy.t(REQUIRED_KEYS[country.id]);
  }

  Object.assign(errors, new HomeCountryRouteValidator().validate(form, config, copy, unsupportedRoute));
  Object.assign(errors, new RouteLocationValidator().validate(form, config, copy));
  Object.assign(errors, new RouteLocationFormatValidator().validate(form, copy));

  const enteredDate = new EnteredDateValidator();
  const invalidDate = copy.t("error.invalidDate");
  const pickupDate = form.pickupDate.trim();
  if (!errors.pickupDate) {
    const invalid = enteredDate.message(pickupDate, invalidDate);
    if (invalid) errors.pickupDate = invalid;
  }
  if (!errors.pickupDate && BoundedDate.isCalendarDate(pickupDate) && pickupDate < todayISODate()) {
    errors.pickupDate = copy.t("error.pickupPast");
  }

  if (fields.isPresent("unloadDate")) {
    const unloadDate = form.unloadDate.trim();
    if (!errors.unloadDate) {
      const invalid = enteredDate.message(unloadDate, invalidDate);
      if (invalid) errors.unloadDate = invalid;
    }
    if (!errors.unloadDate && BoundedDate.isCalendarDate(unloadDate) && unloadDate < todayISODate()) {
      errors.unloadDate = copy.t("error.unloadPast");
    }
    if (
      !errors.unloadDate &&
      BoundedDate.isCalendarDate(pickupDate) &&
      BoundedDate.isCalendarDate(unloadDate) &&
      unloadDate < pickupDate
    ) {
      errors.unloadDate = copy.t("error.unloadBeforePickup");
    }
  }

  const weight = form.totalWeight.trim();
  if (!errors.totalWeight && weight && !inRange(weight, LIMITS.totalWeightKg)) {
    errors.totalWeight = copy.t("error.weightRange", { max: copy.number(LIMITS.totalWeightKg.max) });
  }

  for (const line of form.cargo) {
    if (!inRange(line.qty, LIMITS.qty)) {
      errors[`cargo-${line.id}-qty`] = copy.t("error.rangePlain", {
        min: LIMITS.qty.min,
        max: LIMITS.qty.max,
      });
    }
    if (CargoDimensionMapper.isSizeLocked(line.type)) {
      continue;
    }
    if (CargoDimensionMapper.isLdmOnlyType(line.type) || line.sizeMode === "ldm") {
      if (!inRange(line.ldm, LIMITS.ldm)) {
        errors[`cargo-${line.id}-ldm`] = copy.t("error.rangeLdm", {
          min: LIMITS.ldm.min,
          max: LIMITS.ldm.max,
        });
      }
      continue;
    }
    if (line.height.trim() && !inRange(line.height, LIMITS.heightCm)) {
      errors[`cargo-${line.id}-height`] = copy.t("error.rangeCm", {
        min: LIMITS.heightCm.min,
        max: LIMITS.heightCm.max,
      });
    }
    if (line.type === "custom") {
      if (!inRange(line.length, LIMITS.lengthCm)) {
        errors[`cargo-${line.id}-length`] = copy.t("error.rangePlain", {
          min: LIMITS.lengthCm.min,
          max: LIMITS.lengthCm.max,
        });
      }
      if (!inRange(line.width, LIMITS.widthCm)) {
        errors[`cargo-${line.id}-width`] = copy.t("error.rangePlain", {
          min: LIMITS.widthCm.min,
          max: LIMITS.widthCm.max,
        });
      }
    }
  }

  Object.assign(errors, new ActiveServicesValidator().validate(form, config, copy));

  return errors;
}

const ROUTE_PAGE_IDS = [
  "loadCountry",
  "loadPostal",
  "loadCity",
  "loadStreet",
  "loadHouseNumber",
  "unloadCountry",
  "unloadPostal",
  "unloadCity",
  "unloadStreet",
  "unloadHouseNumber",
  "pickupDate",
  "unloadDate",
] as const;

const SHIPMENT_PAGE_IDS = ["totalWeight", "goods"] as const;

const SERVICE_PAGE_IDS = [
  "adrClass",
  "adrUn",
  "tempMin",
  "tempMax",
  "deliveryDate",
  "loadFrom",
  "loadUntil",
  "loadFixed",
  "unloadFrom",
  "unloadUntil",
  "unloadFixed",
  "tailLoad",
  "attachments",
  "refReference",
  "refLoadReference",
  "refUnloadReference",
] as const;

interface TimePhaseInput {
  mode: TimeMode;
  from: string;
  until: string;
  fixed: string;
  phase: "load" | "unload";
}

export function timePhaseStarted(mode: TimeMode, from: string, until: string, fixed: string): boolean {
  if (mode === "fixed") return Boolean(fixed.trim());
  return Boolean(from.trim() || until.trim());
}

export class ActiveServicesValidator {
  constructor(
    private readonly today: string = todayISODate(),
    private readonly timeField = new TimeFieldValidator(),
  ) {}

  validate(form: FormState, config?: DepartmentConfig, copy: QuoteCopy = QuoteCopy.english()): Errors {
    const errors: Errors = {};
    this.validateAdr(form, errors, copy);
    this.validateTemp(form, errors, config, copy);
    this.validateRef(form, errors, config, copy);
    this.validateTime(form, errors, copy);
    this.validateTail(form, errors, copy);
    this.validateAttachments(form, errors, copy);
    return errors;
  }

  private validateAdr(form: FormState, errors: Errors, copy: QuoteCopy): void {
    if (!form.adr.active) return;
    const un = form.adr.un.trim();
    if (un && !/^\d{4}$/.test(un)) errors.adrUn = copy.t("error.adrUn");
  }

  private validateRef(form: FormState, errors: Errors, config: DepartmentConfig | undefined, copy: QuoteCopy): void {
    if (!config) return;
    const fields = new DepartmentFieldAccess(config);
    if (!fields.requiresReferences() && !form.ref.active) return;
    if (fields.isRequired("reference") && !form.ref.reference.trim()) {
      errors.refReference = copy.t("error.invoiceRef");
    }
    if (fields.isRequired("loadReference") && !form.ref.loadReference.trim()) {
      errors.refLoadReference = copy.t("error.loadingRef");
    }
    if (fields.isRequired("unloadReference") && !form.ref.unloadReference.trim()) {
      errors.refUnloadReference = copy.t("error.unloadingRef");
    }
  }

  private validateTemp(form: FormState, errors: Errors, config: DepartmentConfig | undefined, copy: QuoteCopy): void {
    if (config && !new DepartmentFieldAccess(config).showsTemperature()) return;
    if (!form.temp.active) return;
    this.validateTempBound("tempMin", form.temp.min, copy.t("error.tempMin"), copy.t("error.enterNumber"), errors);
    this.validateTempBound("tempMax", form.temp.max, copy.t("error.tempMax"), copy.t("error.enterNumber"), errors);
    if (!errors.tempMin && !errors.tempMax && Number(form.temp.max) < Number(form.temp.min)) {
      errors.tempMax = copy.t("error.tempOrder");
    }
  }

  private validateTempBound(id: string, raw: string, emptyMessage: string, numberMessage: string, errors: Errors): void {
    if (!raw.trim()) {
      errors[id] = emptyMessage;
      return;
    }
    if (!Number.isFinite(Number(raw))) errors[id] = numberMessage;
  }

  private validateTime(form: FormState, errors: Errors, copy: QuoteCopy): void {
    if (!form.time.active) return;
    const load = {
      mode: form.time.loadType,
      from: form.time.loadFrom,
      until: form.time.loadUntil,
      fixed: form.time.loadFixed,
      phase: "load" as const,
    };
    const unload = {
      mode: form.time.unloadType,
      from: form.time.unloadFrom,
      until: form.time.unloadUntil,
      fixed: form.time.unloadFixed,
      phase: "unload" as const,
    };
    const loadStarted = timePhaseStarted(load.mode, load.from, load.until, load.fixed);
    const unloadStarted = timePhaseStarted(unload.mode, unload.from, unload.until, unload.fixed);
    if (loadStarted) this.validatePhase(load, errors, copy);
    if (unloadStarted) this.validatePhase(unload, errors, copy);
    const deliveryDate = form.time.deliveryDate.trim();
    const invalidDelivery = new EnteredDateValidator().message(deliveryDate, copy.t("error.invalidDate"));
    if (invalidDelivery) {
      errors.deliveryDate = invalidDelivery;
    }
    if (!errors.deliveryDate && BoundedDate.isCalendarDate(deliveryDate) && deliveryDate < this.today) {
      errors.deliveryDate = copy.t("error.deliveryPast");
    }
    if (
      !errors.deliveryDate &&
      BoundedDate.isCalendarDate(form.pickupDate.trim()) &&
      BoundedDate.isCalendarDate(deliveryDate) &&
      deliveryDate < form.pickupDate
    ) {
      errors.deliveryDate = copy.t("error.deliveryBeforePickup");
    }
  }

  private validateTail(form: FormState, errors: Errors, copy: QuoteCopy): void {
    if (!form.tail.active) return;
    if (!form.tail.load && !form.tail.unload) {
      errors.tailLoad = copy.t("error.tailSides");
    }
  }

  private validateAttachments(form: FormState, errors: Errors, copy: QuoteCopy): void {
    if (!form.attachments.active) return;
    if (form.attachments.files.length === 0) {
      errors.attachments = copy.t("error.attachOne");
    }
  }

  private validatePhase(input: TimePhaseInput, errors: Errors, copy: QuoteCopy): void {
    if (input.mode === "fixed") {
      const message = this.timeField.message(
        input.fixed,
        copy.t("error.enterTime"),
        copy.t("error.enterMinutes"),
        copy.t("error.completeTime"),
      );
      if (message) errors[`${input.phase}Fixed`] = message;
      return;
    }
    const from = this.timeField.message(
      input.from,
      copy.t("error.enterStartTime"),
      copy.t("error.enterMinutes"),
      copy.t("error.completeTime"),
    );
    if (from) errors[`${input.phase}From`] = from;
    const until = this.timeField.message(
      input.until,
      copy.t("error.enterEndTime"),
      copy.t("error.enterMinutes"),
      copy.t("error.completeTime"),
    );
    if (until) errors[`${input.phase}Until`] = until;
  }
}

function pickErrors(errors: Errors, ids: readonly string[]): Errors {
  const out: Errors = {};
  for (const id of ids) {
    const message = errors[id];
    if (message) out[id] = message;
  }
  return out;
}

const CONTACT_PAGE_IDS = ["contactName", "company", "email", "phone", "comment", "privacyConsent"] as const;

export function pickPageErrors(errors: Errors, page: ManualPage): Errors {
  if (page === "route") return pickErrors(errors, ROUTE_PAGE_IDS);
  if (page === "shipment") {
    const out = pickErrors(errors, SHIPMENT_PAGE_IDS);
    for (const id of Object.keys(errors)) {
      if (id.startsWith("cargo-")) out[id] = errors[id];
    }
    return out;
  }
  if (page === "services") return pickErrors(errors, SERVICE_PAGE_IDS);
  return pickErrors(errors, CONTACT_PAGE_IDS);
}

const FIELD_DOM_IDS: Record<string, string> = {
  adrClass: "nv-adr-class",
  adrUn: "nv-adr-un",
  tempMin: "nv-temp-min",
  tempMax: "nv-temp-max",
  deliveryDate: "nv-time-deadline",
  loadFrom: "nv-time-load-from",
  loadUntil: "nv-time-load-until",
  loadFixed: "nv-time-load-fixed",
  unloadFrom: "nv-time-unload-from",
  unloadUntil: "nv-time-unload-until",
  unloadFixed: "nv-time-unload-fixed",
  tailLoad: "nv-tail-load",
  attachments: "nv-attach-btn",
  refReference: "nv-ref-reference",
  refLoadReference: "nv-ref-load",
  refUnloadReference: "nv-ref-unload",
  privacyConsent: "nv-privacy",
};

export function fieldDomId(errorId: string): string {
  return FIELD_DOM_IDS[errorId] ?? `nv-${errorId}`;
}

export function firstInvalidPage(errors: Errors): ManualPage | null {
  for (const page of ["route", "shipment", "services", "contact"] as const) {
    if (Object.keys(pickPageErrors(errors, page)).length > 0) return page;
  }
  return null;
}

export function firstInvalidFieldId(errors: Errors, page?: ManualPage): string | undefined {
  const scoped = page ? pickPageErrors(errors, page) : pageErrorsFor(firstInvalidPage(errors), errors);
  return Object.keys(scoped)[0];
}

function pageErrorsFor(page: ManualPage | null, errors: Errors): Errors {
  if (!page) return {};
  return pickPageErrors(errors, page);
}

class ContactEmailListMapper {
  segments(raw: string): string[] {
    return raw.split(/[,;]/).map((part) => part.trim());
  }
}

class EmailAddressValidator {
  constructor(private readonly personal = new PersonalEmailDomain()) {}

  rejectReason(raw: string, copy: QuoteCopy = QuoteCopy.english()): string | null {
    const email = raw.trim();
    if (!email) return copy.t("error.enterEmail");
    if (!isEmail(email)) return copy.t("error.validEmail");
    return this.personal.rejectReason(email) ? copy.t("error.personalEmail") : null;
  }
}

export class ContactEmailsValidator {
  constructor(
    private readonly emails = new ContactEmailListMapper(),
    private readonly address = new EmailAddressValidator(),
  ) {}

  rejectReason(raw: string, copy: QuoteCopy = QuoteCopy.english()): string | null {
    if (!raw.trim()) return copy.t("error.enterEmail");
    const segments = this.emails.segments(raw);
    if (segments.some((part) => part.length === 0)) return copy.t("error.validEmail");
    for (const email of segments) {
      const reason = this.address.rejectReason(email, copy);
      if (reason) return reason;
    }
    return null;
  }
}

export class PhoneNumberValidator {
  private static readonly MIN_DIGITS = 8;
  private static readonly MAX_DIGITS = 15;

  rejectReason(raw: string, copy: QuoteCopy = QuoteCopy.english()): string | null {
    const phone = raw.trim();
    if (!phone) return null;
    if (/[a-zA-Z]/.test(phone)) return copy.t("error.validPhone");
    const digits = phone.replace(/\D/g, "");
    if (digits.length < PhoneNumberValidator.MIN_DIGITS || digits.length > PhoneNumberValidator.MAX_DIGITS) {
      return copy.t("error.validPhone");
    }
    return null;
  }
}

export class QuickFillInputValidator {
  validate(text: string, files: File[], copy: QuoteCopy = QuoteCopy.english()): string | null {
    if (text.trim() || files.length > 0) return null;
    return copy.t("error.quickFillEmpty");
  }
}

export class QuickFillFileValidator {
  constructor(private readonly limits = QUICK_FILL_LIMITS) {}

  rejectReason(existing: File[], incoming: File[], copy: QuoteCopy = QuoteCopy.english()): string | null {
    if (existing.length + incoming.length > this.limits.maxFiles) {
      return copy.t("error.maxFiles", { max: this.limits.maxFiles });
    }
    const maxBytes = this.limits.maxFileMb * 1024 * 1024;
    if (incoming.some((file) => file.size > maxBytes)) {
      return copy.t("error.maxFileMb", { max: this.limits.maxFileMb });
    }
    return null;
  }
}

export class AcceptedAttachmentFilter {
  static readonly TYPES = [
    "application/pdf",
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
  ] as const;

  accepted(file: File): boolean {
    if (file.name.toLowerCase().endsWith(".svg")) return false;
    const type = file.type.toLowerCase();
    if (!type || type === "application/octet-stream") return true;
    return AcceptedAttachmentFilter.TYPES.some((allowed) => type === allowed);
  }
}

export class AttachmentMagic {
  detect(bytes: Uint8Array): string | null {
    if (bytes.length >= 4 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
      return "application/pdf";
    }
    if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
      return "image/jpeg";
    }
    if (
      bytes.length >= 8 &&
      bytes[0] === 0x89 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x4e &&
      bytes[3] === 0x47
    ) {
      return "image/png";
    }
    if (bytes.length >= 6 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
      return "image/gif";
    }
    if (
      bytes.length >= 12 &&
      bytes[0] === 0x52 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x46 &&
      bytes[8] === 0x57 &&
      bytes[9] === 0x45 &&
      bytes[10] === 0x42 &&
      bytes[11] === 0x50
    ) {
      return "image/webp";
    }
    return null;
  }
}

export class QuoteAttachmentFileValidator {
  constructor(private readonly limits = QUOTE_ATTACHMENT_LIMITS) {}

  rejectReason(existing: File[], incoming: File[], copy: QuoteCopy = QuoteCopy.english()): string | null {
    if (existing.length + incoming.length > this.limits.maxFiles) {
      return copy.t("error.maxFiles", { max: this.limits.maxFiles });
    }
    const maxBytes = this.limits.maxFileMb * 1024 * 1024;
    if (incoming.some((file) => file.size > maxBytes)) {
      return copy.t("error.maxFileMb", { max: this.limits.maxFileMb });
    }
    const total = [...existing, ...incoming].reduce((sum, file) => sum + file.size, 0);
    if (total > this.limits.maxTotalBytes) {
      return copy.t("error.maxTotalMb", { max: this.limits.maxTotalMb });
    }
    return null;
  }
}

type QuoteAttachmentIntakeResult =
  | { changed: false; feedback: string }
  | { changed: true; files: File[]; feedback: string };

export class QuoteAttachmentIntake {
  constructor(
    private readonly types = new AcceptedAttachmentFilter(),
    private readonly limits = new QuoteAttachmentFileValidator(),
    private readonly magic = new AttachmentMagic(),
  ) {}

  async add(existing: File[], incoming: File[], copy: QuoteCopy = QuoteCopy.english()): Promise<QuoteAttachmentIntakeResult> {
    if (incoming.length === 0) {
      return { changed: false, feedback: "" };
    }
    const empty = incoming.filter((file) => file.size === 0);
    const nonempty = incoming.filter((file) => file.size > 0);
    const sniffed = await Promise.all(nonempty.map((file) => this.usable(file)));
    const accepted = nonempty.filter((_, index) => sniffed[index]);
    const skipped = nonempty.length - accepted.length;
    if (!accepted.length) {
      return { changed: false, feedback: this.noneAcceptedFeedback(empty.length, nonempty.length, copy) };
    }
    const rejected = this.limits.rejectReason(existing, accepted, copy);
    if (rejected) {
      return { changed: false, feedback: rejected };
    }
    let feedback = "";
    if (skipped > 0) {
      feedback = copy.t("error.filesSkippedType");
    } else if (empty.length > 0) {
      feedback = copy.t("error.emptySkipped");
    }
    return { changed: true, files: [...existing, ...accepted], feedback };
  }

  private noneAcceptedFeedback(emptyCount: number, nonemptyCount: number, copy: QuoteCopy): string {
    if (emptyCount && !nonemptyCount) {
      return copy.t("error.fileEmpty");
    }
    if (emptyCount && nonemptyCount) {
      return copy.t("error.filesEmptyOrType");
    }
    return copy.t("error.attachType");
  }

  private async usable(file: File): Promise<boolean> {
    if (!this.types.accepted(file)) return false;
    const bytes = new Uint8Array(await file.slice(0, 16).arrayBuffer());
    return this.magic.detect(bytes) !== null;
  }
}

type SubjectField = "loadCity" | "loadPostal" | "unloadCity" | "unloadPostal" | "company";
type BodyField = "goods" | "comment";

class QuoteTextValidator {
  constructor(private readonly limits = QUOTE_TEXT_LIMITS) {}

  private rejectSubject(value: string): string | null {
    if (value.length > this.limits.maxSubject) return "Subject is too long.";
    return null;
  }

  private rejectEmailText(value: string): string | null {
    if (value.length > this.limits.maxEmailText) return "Request details are too long.";
    return null;
  }

  validate(form: FormState, copy: QuoteCopy = QuoteCopy.english()): Errors {
    const errors: Errors = {};
    if (this.rejectSubject(buildSubject(form))) {
      errors[this.longestSubjectField(form)] = copy.t("error.shortenSubject", {
        max: this.limits.maxSubject,
      });
    }
    if (this.rejectEmailText(generateEmailText(form))) {
      errors[this.longestBodyField(form)] = copy.t("error.shortenBody", {
        max: copy.number(this.limits.maxEmailText),
      });
    }
    return errors;
  }

  private longestSubjectField(form: FormState): SubjectField {
    const load = form.loadCity.trim()
      ? (["loadCity", form.loadCity] as const)
      : (["loadPostal", form.loadPostal] as const);
    const unload = form.unloadCity.trim()
      ? (["unloadCity", form.unloadCity] as const)
      : (["unloadPostal", form.unloadPostal] as const);
    const fields: Array<readonly [SubjectField, string]> = [
      load,
      unload,
      ["company", form.company],
    ];
    return fields.reduce((longest, current) =>
      current[1].length > longest[1].length ? current : longest,
    )[0];
  }

  private longestBodyField(form: FormState): BodyField {
    return form.goods.length > form.comment.length ? "goods" : "comment";
  }
}

const phoneValidator = new PhoneNumberValidator();
const emailValidator = new ContactEmailsValidator();
const quoteTextValidator = new QuoteTextValidator();

export function validateContact(form: FormState, copy: QuoteCopy = QuoteCopy.english()): Errors {
  const errors: Errors = {};
  if (!form.contactName.trim()) errors.contactName = copy.t("error.enterName");
  if (!form.company.trim()) errors.company = copy.t("error.enterCompany");
  const emailError = emailValidator.rejectReason(form.email, copy);
  if (emailError) errors.email = emailError;
  const phoneError = phoneValidator.rejectReason(form.phone, copy);
  if (phoneError) errors.phone = phoneError;
  if (!form.privacyConsent) errors.privacyConsent = copy.t("error.privacy");
  return errors;
}

export function validateQuoteText(form: FormState, copy: QuoteCopy = QuoteCopy.english()): Errors {
  return quoteTextValidator.validate(form, copy);
}
